import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { TextArea } from '@shared/components/ui/textarea';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { useBankAccountMutations } from '../../hooks/useBankAccountMutations';
import type { BankAccount } from '../../models/BankAccount';

export interface RejectBankAccountModalRef {
  open: (account: BankAccount) => void;
  close: () => void;
}

interface RejectBankAccountModalProps {
  onSuccess?: () => void;
}

export const RejectBankAccountModal = forwardRef<RejectBankAccountModalRef, RejectBankAccountModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('bankAccountsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const { rejectBankAccount } = useBankAccountMutations();

    const [account, setAccount] = useState<BankAccount | null>(null);
    const [reason, setReason] = useState('');

    useImperativeHandle(ref, () => ({
      open: (acc: BankAccount) => {
        setAccount(acc);
        setReason('');
        modalRef.current?.openModal();
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    const handleReject = async () => {
      if (!account || !reason.trim()) return;

      try {
        await rejectBankAccount.mutateAsync({
          id: account.id,
          payload: { rejected_reason: reason.trim() },
        });

        toast({
          title: t('modals.reject.success.title'),
          description: t('modals.reject.success.description'),
        });

        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.rejectFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = rejectBankAccount.isPending;

    return (
      <ModalCenter ref={modalRef} title={t('modals.reject.title')} width="default">
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <FontAwesomeIcon icon={faTimesCircle} className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-sm text-foreground">{t('modals.reject.description')}</p>
          </div>

          {account && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <p className="text-sm">
                <span className="font-medium">Titular:</span> {account.holder_name}
              </p>
              <p className="text-sm font-mono">
                <span className="font-medium font-sans">IBAN:</span> {account.iban}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.reject.fields.reason')} *</label>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('modals.reject.fields.reason_placeholder')}
              disabled={isLoading}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={!reason.trim() || isLoading}>
              {isLoading ? t('buttons.rejecting') : t('actions.reject')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

RejectBankAccountModal.displayName = 'RejectBankAccountModal';
